import { ArrowRight, Building2, ExternalLink, Target } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SponsorLeaderboard } from "../components/intelligence/SponsorLeaderboard";
import { Badge } from "../components/ui/Badge";
import { PageIntro } from "../components/ui/PageIntro";
import { Pagination } from "../components/ui/Pagination";
import { SearchBox } from "../components/ui/SearchBox";
import { SectionHeading } from "../components/ui/SectionHeading";
import { SourceNote } from "../components/ui/SourceNote";
import { useAppData } from "../context/AppDataContext";
import { activeStatuses, prettyEnum } from "../lib/format";
import type { Asset, Trial } from "../types";

const chartColors = [
  "#d6dbff",
  "#b7c0ff",
  "#98a5fb",
  "#7c8deb",
  "#6174d5",
  "#485cbc",
];

export function PipelinePage() {
  const { summary, trials, assets, strategic } = useAppData();
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState("ALL");
  const [page, setPage] = useState(1);
  const pageSize = 9;
  const targetChart = strategic.targetLandscape
    .slice(0, 6)
    .map((item) => ({ name: item.target, value: item.activeTrials }))
    .sort((a, b) => a.value - b.value);
  const targets = [
    ...new Set(
      assets
        .filter(
          (asset) =>
            asset.activeTrialCount > 0 && asset.target !== "Unclassified",
        )
        .map((asset) => asset.target),
    ),
  ].sort();
  const filtered = assets.filter(
    (asset) =>
      asset.activeTrialCount > 0 &&
      (target === "ALL" || asset.target === target) &&
      `${asset.name} ${asset.target} ${asset.modality} ${asset.sponsors.join(" ")}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
  const trialById = useMemo(
    () => new Map(trials.map((trial) => [trial.nctId, trial])),
    [trials],
  );
  useEffect(() => setPage(1), [query, target]);

  return (
    <section className="mx-auto max-w-[1200px] px-6 py-16">
      <PageIntro
        title="Pipeline structure and sponsor activity"
        copy="A therapy-centric view of active interventional studies, target families, modalities, sponsors, and registered geographic activity."
      />
      <div className="grid gap-4 lg:grid-cols-[1.35fr_.8fr]">
        <article className="rounded-lg border border-[#dbe5e1] bg-white p-6">
          <h2 className="text-[15px] font-bold text-[#0b292f]">
            Active trial mix by development phase
          </h2>
          <p className="mt-1 text-xs text-[#65797b]">
            Active interventional studies may report more than one phase
            designation.
          </p>
          <div className="mt-5 h-72">
            <ResponsiveContainer>
              <BarChart data={summary.countsByPhase}>
                <CartesianGrid
                  stroke="#2a3036"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  axisLine={false}
                  dataKey="name"
                  tick={{ fill: "#9ba3aa", fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  tick={{ fill: "#9ba3aa", fontSize: 11 }}
                  tickLine={false}
                />
                <Tooltip cursor={{ fill: "#191e23" }} />
                <Bar dataKey="value" fill="#788aff" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>
        <article className="rounded-lg border border-[#dbe5e1] bg-white p-6">
          <h2 className="text-[15px] font-bold text-[#0b292f]">
            Target-family distribution
          </h2>
          <p className="mt-1 text-xs text-[#65797b]">
            Unique active trials consolidated across target-specific modalities.
          </p>
          <div className="mt-4 grid items-center sm:grid-cols-[1fr_.85fr] lg:grid-cols-1 xl:grid-cols-[1fr_.85fr]">
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={targetChart}
                    dataKey="value"
                    innerRadius={55}
                    nameKey="name"
                    outerRadius={82}
                    paddingAngle={2}
                  >
                    {targetChart.map((_, index) => (
                      <Cell
                        fill={chartColors[index % chartColors.length]}
                        key={index}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {targetChart.map((item, index) => (
                <div
                  className="grid grid-cols-[8px_1fr_auto] items-center gap-2 text-[11px]"
                  key={item.name}
                >
                  <i
                    className="size-2 rounded-full"
                    style={{ background: chartColors[index] }}
                  />
                  <span>{item.name}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </article>
      </div>

      <div className="relative z-10 -mt-5 mb-16 grid overflow-hidden rounded-lg border border-white/10 bg-[#102e33] text-white shadow-xl md:grid-cols-3">
        <article className="border-b border-white/10 p-5 md:border-b-0 md:border-r">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8fe3c5]">
            Top-five sponsor share
          </span>
          <strong className="mt-2 block [font-family:Newsreader] text-[28px] font-medium">
            {strategic.top5SponsorShare}%
          </strong>
          <p className="mt-1 text-[11px] text-[#a8bdbd]">
            of active interventional studies
          </p>
        </article>
        <article className="border-b border-white/10 p-5 md:border-b-0 md:border-r">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8fe3c5]">
            Highest active-study lead-sponsor count
          </span>
          <strong className="mt-2 block [font-family:Newsreader] text-xl font-medium">
            {strategic.topSponsors[0]?.name}
          </strong>
          <p className="mt-1 text-[11px] text-[#a8bdbd]">
            {strategic.topSponsors[0]?.activeTrials} active trials
          </p>
        </article>
        <article className="p-5">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#8fe3c5]">
            Highest registered site count
          </span>
          <strong className="mt-2 block [font-family:Newsreader] text-xl font-medium">
            {strategic.geographicFootprint[0]?.country}
          </strong>
          <p className="mt-1 text-[11px] text-[#a8bdbd]">
            {strategic.geographicFootprint[0]?.activeTrials} active studies with
            a site
          </p>
        </article>
      </div>

      <SectionHeading
        title="Lead sponsors grouped by registry class"
        copy="ClinicalTrials.gov sponsor classifications are shown separately for industry and non-industry organizations. Counts are active interventional studies attributed to each lead sponsor."
      />
      <div className="mb-16 grid gap-4 lg:grid-cols-2">
        <SponsorLeaderboard
          kind="industry"
          title="Industry-sponsored development"
          subtitle="Industry-classified sponsors by active interventional study count"
          sponsors={strategic.industrySponsors.slice(0, 8)}
        />
        <SponsorLeaderboard
          kind="institution"
          title="Institutional research activity"
          subtitle="Non-industry sponsors, including government, academic, and network organizations"
          sponsors={strategic.institutionSponsors.slice(0, 8)}
        />
      </div>

      <SectionHeading
        title="Search active therapy and regimen entities"
        copy="Programs are normalized where reviewed aliases exist. Each card links to its matching study set and a recently updated source record; unresolved development codes remain separate to avoid false entity merges."
      />
      <div className="grid gap-2.5 sm:grid-cols-[1fr_240px]">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Search asset, target, modality, or sponsor"
        />
        <label className="rounded-md border border-[#dbe5e1] bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#718587]">
          Target
          <select
            className="mt-1 block w-full border-0 bg-transparent text-[12px] font-semibold normal-case tracking-normal text-[#0b292f] outline-none"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          >
            <option value="ALL">All classified targets</option>
            {targets.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="my-3 text-xs text-[#718587]">
        <strong className="text-[#0b292f]">
          {filtered.length.toLocaleString()}
        </strong>{" "}
        active program entities
      </p>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {pageItems.map((asset) => {
          const sourceTrial = latestSourceTrial(asset, trialById);
          return (
            <article
              className="flex h-full flex-col rounded-lg border border-[#dbe5e1] bg-white p-5 transition duration-200 hover:-translate-y-0.5 hover:border-[#b9d4cb] hover:shadow-[0_12px_28px_rgba(19,70,59,.08)] motion-reduce:transform-none"
              key={asset.id}
            >
              <div className="flex items-start justify-between">
                <span className="grid size-9 place-items-center rounded-full bg-[#e4f3ed] text-[#158c77]">
                  <Target size={16} />
                </span>
                <Badge tone="blue">{prettyEnum(asset.highestPhase)}</Badge>
              </div>
              <h3 className="mt-4 line-clamp-2 min-h-10 text-pretty text-[15px] font-bold leading-5 text-[#0b292f]">
                {asset.name}
              </h3>
              <p className="mt-1 text-[11px] text-[#65797b]">
                {asset.modality} · {asset.target}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {asset.sponsors.slice(0, 2).map((sponsor) => (
                  <span
                    className="inline-flex items-center gap-1 rounded bg-[#f0f4f1] px-2 py-1 text-[9px] text-[#5f7476]"
                    key={sponsor}
                  >
                    <Building2 size={11} />
                    {sponsor}
                  </span>
                ))}
              </div>
              <div className="mt-auto flex gap-5 pt-5 text-[10px] text-[#718587]">
                <span>
                  <strong className="mr-1 text-sm text-[#0b292f]">
                    {asset.activeTrialCount}
                  </strong>
                  active studies
                </span>
                <span>
                  <strong className="mr-1 text-sm text-[#0b292f]">
                    {asset.recruitingTrialCount}
                  </strong>
                  recruiting
                </span>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#e3eae6] pt-3">
                <Link
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#158c77] no-underline hover:text-[#0b292f]"
                  to={`/trials?query=${encodeURIComponent(asset.name)}`}
                >
                  View {asset.trialCount.toLocaleString()} linked{" "}
                  {asset.trialCount === 1 ? "study" : "studies"}
                  <ArrowRight size={12} />
                </Link>
                {sourceTrial && (
                  <a
                    aria-label={`Open the most recently updated linked ClinicalTrials.gov record for ${asset.name}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#718587] no-underline hover:text-[#158c77]"
                    href={sourceTrial.sourceUrl}
                    rel="noreferrer"
                    target="_blank"
                    title={`Open ${sourceTrial.nctId} in ClinicalTrials.gov`}
                  >
                    Latest source
                    <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <Pagination
        page={page}
        total={filtered.length}
        pageSize={pageSize}
        onPage={setPage}
        label="programs"
      />
      <SourceNote>
        Counts reflect the documented disease query and registered lead-sponsor
        attribution. They do not represent market share, development investment,
        or probability of success.
      </SourceNote>
    </section>
  );
}

function latestSourceTrial(asset: Asset, trialById: Map<string, Trial>) {
  let latestActive: Trial | undefined;
  let latestAny: Trial | undefined;
  for (const id of asset.trialIds) {
    const trial = trialById.get(id);
    if (!trial) continue;
    if (
      !latestAny ||
      (trial.lastUpdated ?? "") > (latestAny.lastUpdated ?? "")
    ) {
      latestAny = trial;
    }
    if (
      trial.studyType === "INTERVENTIONAL" &&
      activeStatuses.has(trial.status) &&
      (!latestActive ||
        (trial.lastUpdated ?? "") > (latestActive.lastUpdated ?? ""))
    ) {
      latestActive = trial;
    }
  }
  return latestActive ?? latestAny;
}
